import { Search, X } from 'lucide-react';
import { TableHead } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { DataTableColumn } from './types';

interface ColumnFilterCellProps<T, Ctx> {
  col: DataTableColumn<T, Ctx>;
  columnFilters: Record<string, string>;
  setColumnFilter: (key: string, value: string) => void;
}

/**
 * Renders the filter cell for a single column.
 *
 * Variants (style-guide §3.3, §3.3a, §3.4):
 *   - isPrimarySearch  → magnifier + clear (X) button
 *   - filterType=select → multi-select dropdown with "All X" / single / N label
 *   - filterType=text (default) → plain input
 *   - noFilter → empty cell
 */
export function ColumnFilterCell<T, Ctx>({
  col,
  columnFilters,
  setColumnFilter,
}: ColumnFilterCellProps<T, Ctx>) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  // ─── Primary-search variant ───────────────────────────────────────
  if (col.isPrimarySearch) {
    const value = columnFilters[col.key] || '';
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={value}
            onChange={(e) => setColumnFilter(col.key, e.target.value)}
            placeholder={col.searchPlaceholder ?? 'Search...'}
            className="h-7 pl-7 pr-7 text-xs"
          />
          {value && (
            <button
              type="button"
              onClick={() => setColumnFilter(col.key, '')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </TableHead>
    );
  }

  // ─── Multi-select variant ─────────────────────────────────────────
  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    const triggerLabel =
      selected.length === 0
        ? (col.filterAllLabel ?? `All ${col.label}`)
        : selected.length === 1
          ? (col.filterOptions.find((o) => o.value === selected[0])?.label ?? '1 selected')
          : `${selected.length} selected`;

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs justify-start font-normal"
            >
              {triggerLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-48 max-h-64 overflow-y-auto">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
            {selected.length > 0 && (
              <DropdownMenuItem
                className="text-xs text-muted-foreground mt-1 border-t"
                onClick={() => setColumnFilter(col.key, '')}
              >
                Clear
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // ─── Plain text filter (default) ──────────────────────────────────
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}
